import { useState } from 'react';
import { ListChecks, CheckSquare, Square, Loader2 } from 'lucide-react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { Modal, ModalFooter } from './Modal';

interface CreateChecklistModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (name: string, description: string | undefined, importAfter: boolean) => void;
  isPending: boolean;
}

export function CreateChecklistModal({
  isOpen,
  onClose,
  onSubmit,
  isPending,
}: CreateChecklistModalProps) {
  const { t } = useLanguage();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [importAfterCreate, setImportAfterCreate] = useState(false);

  const handleSubmit = () => {
    if (!name.trim()) return;
    onSubmit(name.trim(), description.trim() || undefined, importAfterCreate);
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setImportAfterCreate(false);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('checklists.createChecklist', 'Create Checklist')}
      icon={ListChecks}
      iconColor="primary"
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="checklist-name" className="block text-sm font-medium text-gray-700 mb-2">
            {t('checklists.name', 'Name')}
          </label>
          <input
            id="checklist-name"
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={t('checklists.namePlaceholder', 'e.g., Living Room Renovation')}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow"
          />
        </div>

        <div>
          <label htmlFor="checklist-description" className="block text-sm font-medium text-gray-700 mb-2">
            {t('checklists.descriptionOptional', 'Description (optional)')}
          </label>
          <textarea
            id="checklist-description"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder={t('checklists.descriptionPlaceholder', 'Add a description...')}
            rows={3}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow resize-none"
          />
        </div>

        <button
          type="button"
          onClick={() => setImportAfterCreate(!importAfterCreate)}
          className="flex items-center gap-3 p-3 w-full text-left rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors"
        >
          {importAfterCreate ? (
            <CheckSquare className="w-5 h-5 text-primary-600" />
          ) : (
            <Square className="w-5 h-5 text-gray-400" />
          )}
          <span className="text-sm text-gray-700">
            {t('checklists.createFromFile', 'Create from file (PDF or CSV)')}
          </span>
        </button>

        <ModalFooter>
          <button
            onClick={handleClose}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isPending || !name.trim()}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {t('checklists.create', 'Create')}
          </button>
        </ModalFooter>
      </div>
    </Modal>
  );
}
